import { z } from "zod";

// Transfer (stamp) duty calculator — NSW / VIC / QLD MVP.
//
// Architecture rule #1: rules are *data*, versioned per state + effective date,
// kept in git, unit-tested against the state revenue offices' own worked
// examples (the moat). Calculation functions are pure: `inputs -> rules ->
// outputs`. No I/O, no `Date.now()` — the contract/assessment date is always a
// parameter so historical re-runs resolve against the schedule in force then.
//
// NOTE: stamp duty is a STATE TAX, not a financial product (s764A perimeter is
// not engaged). But this surface is the archetypal mortgage-broker lead hook —
// see the regulatory ship-condition in `docs/research/discovery/panels/
// stamp-duty-calculator.md §2`. The hard NCCP guard lives in `calculate.ts`
// (`assertNoCreditHandoff`): no credit / financial-product / broker / lender
// identifier may ride alongside any surface that shows a duty figure. Tested.

/** The three states shipped in the MVP. WA/SA/ACT/TAS/NT are staged for v2. */
export const STAMP_DUTY_STATES = ["NSW", "VIC", "QLD"] as const;
export type StampDutyState = (typeof STAMP_DUTY_STATES)[number];

/** Buyer residency for the foreign-purchaser surcharge line. */
export const RESIDENCY = ["domestic", "foreign"] as const;
export type Residency = (typeof RESIDENCY)[number];

/**
 * Property/build type. Material for QLD: from 1 May 2025 a first-home buyer of a
 * NEW (or substantially renovated, never-occupied) home pays nil duty with NO
 * value cap, whereas the established-home first-home concession tapers from
 * $700k to $800k. The $700k threshold is the established-home figure.
 */
export const BUILD_TYPE = ["established", "new"] as const;
export type BuildType = (typeof BUILD_TYPE)[number];

/**
 * A marginal transfer-duty bracket. Duty within the bracket is
 * `base + rate × (dutiableValue − over)`, where `over` is the published
 * "for every $1 over $X" threshold (usually `from − 1` once the table is
 * authored as half-open `[from, to)` bands).
 */
export interface DutyBracket {
  /** Lower bound of dutiable value for this bracket, inclusive. */
  from: number;
  /** Upper bound, exclusive. `null` = unbounded (top bracket). */
  to: number | null;
  /** Accumulated duty at exactly `over` (whole dollars). */
  base: number;
  /** Marginal rate within the bracket (fraction, e.g. 0.055 = $5.50 per $100). */
  rate: number;
  /** "for every $1 over $X" threshold the marginal rate applies above. */
  over: number;
  /**
   * When true the bracket charges `rate × whole dutiable value` rather than
   * `base + rate × (value − over)`. Used for VIC's flat 5.5% $960k–$2m band,
   * which is a "percentage of the whole value", not a marginal step.
   */
  wholeValue?: boolean;
}

/** First-home exemption/concession schedule (state-specific). */
export interface FirstHomeConcession {
  /** Dutiable value at/below which duty is fully exempt (nil). */
  fullExemptionUpTo: number;
  /**
   * Upper bound of the concession taper. Above this, full duty applies (no
   * concession). Between `fullExemptionUpTo` and `taperEndsAt` a reduced duty
   * applies per `taper`.
   */
  taperEndsAt: number;
  /**
   * How the concession is computed in the taper band.
   *  - "vic-linear": concessional duty = generalDuty × (value − exemptUpTo) /
   *    (taperEndsAt − exemptUpTo). (SRO Victoria sliding-scale formula.)
   *  - "nsw-fhbas": a concessional-rate formula RevNSW publishes; modelled here
   *    as a linear fraction of full duty across the taper band (same shape as
   *    VIC), which the build boundary-tests against RevNSW.
   *  - "qld-saving": QLD first-home concession expressed as a fixed maximum
   *    duty SAVING (dollars) that phases out linearly to $0 across the taper
   *    band. Concessional duty = generalDuty − saving.
   */
  taper: "vic-linear" | "nsw-fhbas" | "qld-saving";
  /** Max duty saving (QLD established homes only). Phases to $0 by taperEndsAt. */
  maxSaving?: number;
}

/** Versioned, dated transfer-duty ruleset for one state. */
export interface StampDutyRuleSet {
  state: StampDutyState;
  /** Human label of the period, e.g. "2025-26". */
  period: string;
  /** Inclusive ISO start date this schedule is effective from. */
  effectiveFrom: string;
  /** Inclusive ISO end date, or null if open-ended/current. */
  effectiveTo: string | null;
  /** Source-of-truth URLs the figures were transcribed from. */
  sources: Record<string, string>;
  /** Date the source pages were last verified, ISO. */
  sourcesVerifiedAt: string;
  /** General transfer-duty brackets, authored to cover [0, ∞). */
  brackets: readonly DutyBracket[];
  /** Foreign-purchaser surcharge rate on dutiable value (fraction). */
  foreignSurchargeRate: number;
  /** ISO date the current foreign-surcharge rate took effect. */
  foreignSurchargeFrom: string;
  /** First-home exemption/concession schedule for established homes. */
  firstHome: FirstHomeConcession;
  /**
   * QLD only: from 1 May 2025 a first-home buyer of a NEW home pays nil duty
   * with no value cap. `null` for states without a new-build branch.
   */
  firstHomeNewBuild: {
    /** ISO date the uncapped nil-duty new-build concession took effect. */
    from: string;
    /** No value cap when true. */
    uncapped: true;
  } | null;
}

export const TransferDutyInputSchema = z.object({
  state: z.enum(STAMP_DUTY_STATES),
  /** Purchase price / dutiable value in whole dollars. */
  dutiableValue: z.number().min(0),
  /** Buyer residency — drives the foreign-purchaser surcharge line. */
  residency: z.enum(RESIDENCY).default("domestic"),
});
/** Caller-facing input — defaulted fields are optional (z.input). */
export type TransferDutyInput = z.input<typeof TransferDutyInputSchema>;

export interface TransferDutyOutput {
  state: StampDutyState;
  dutiableValue: number;
  /** General transfer duty before any surcharge, whole dollars. */
  generalDuty: number;
  /** Foreign-purchaser surcharge duty, whole dollars (0 when domestic). */
  foreignSurcharge: number;
  /** generalDuty + foreignSurcharge. */
  totalDuty: number;
  /** The bracket the value fell in (for per-bracket breakdown transparency). */
  bracket: {
    /** Inclusive label, e.g. "$540,001–$1,000,000". */
    label: string;
    base: number;
    rate: number;
    over: number;
  };
}

export const FirstHomeDutyInputSchema = z.object({
  state: z.enum(STAMP_DUTY_STATES),
  dutiableValue: z.number().min(0),
  residency: z.enum(RESIDENCY).default("domestic"),
  /** Established vs new build — material for the QLD new-build branch. */
  buildType: z.enum(BUILD_TYPE).default("established"),
});
/** Caller-facing input — defaulted fields are optional (z.input). */
export type FirstHomeDutyInput = z.input<typeof FirstHomeDutyInputSchema>;

export interface FirstHomeDutyOutput {
  state: StampDutyState;
  dutiableValue: number;
  buildType: BuildType;
  /** Full general transfer duty (no concession), whole dollars. */
  fullDuty: number;
  /** Duty payable WITH the first-home concession/exemption, whole dollars. */
  concessionDuty: number;
  /**
   * The dollar difference between the two CITED figures above
   * (`fullDuty − concessionDuty`). Presented as a computed difference between
   * two cited duty figures — never a "you save $X" claim. >= 0.
   */
  concessionValue: number;
  /** Whether any first-home concession/exemption applied at all. */
  concessionApplies: boolean;
  /**
   * Which branch produced the concession figure — for transparent copy:
   *  - "full-exemption": value at/below the nil-duty threshold.
   *  - "taper": within the sliding-scale concession band.
   *  - "none": above the taper — full duty, no concession.
   *  - "qld-new-build": QLD new home, 1 May 2025+ uncapped nil duty.
   */
  branch: "full-exemption" | "taper" | "none" | "qld-new-build";
}
